import { useApplication, useFileUpload } from "@ca-dmv-radv/data";
import { useMemo } from "react";
import { isMobile } from "react-device-detect";
import { Documents, isAbbyyFlow, isDocument } from "../helpers/constants";

const CATEGORY = {
  1: "PASSPORT",
  2: "PASSPORT_CARD",
  61: "PASSPORT",
  62: "PASSPORT_CARD",
  82: "PASSPORT",
  83: "PASSPORT_CARD",
  120: "PASSPORT",
  121: "PASSPORT_CARD",
  170: "PASSPORT",
  171: "PASSPORT_CARD",
  213: "PASSPORT",
  218: "PASSPORT_CARD",
};
const MIN_LENGTH = {
  PASSPORT: 6,
  PASSPORT_CARD: 9,
};
const MAX_LENGTH = {
  PASSPORT: 9,
  PASSPORT_CARD: 9,
};

export default function useVerificationFieldsValidation() {
  const { birthCertificateFormData, passportFormData, expirationDateFormData } =
    useFileUpload();
  const { documentTypes } = useApplication();

  // Resolve PASSPORT / PASSPORT_CARD from the document type returned by the
  // API so new application types (e.g. RNC) work without hardcoding their
  // catDocIds. Falls back to the known catDocId map.
  const getPassportCategory = (categoryId) => {
    const documentType = documentTypes?.[categoryId];
    if (documentType && isDocument(Documents.PASSPORT_CARD, documentType)) {
      return "PASSPORT_CARD";
    }
    if (documentType && isDocument(Documents.PASSPORT, documentType)) {
      return "PASSPORT";
    }
    return CATEGORY[categoryId];
  };

  /**
   * Checks Birth Certificate form fields are not empty
   * Used to enable/disable the modal submit button
   */
  const validBCForm =
    !birthCertificateFormData.State_Of_Birth ||
    (!birthCertificateFormData.State_File_Number &&
      birthCertificateFormData.State_File_Nbr_Chk === "false") ||
    (!birthCertificateFormData.Registrar_Number &&
      birthCertificateFormData.Registrar_Nbr_Chk === "false") ||
    (birthCertificateFormData.State_Of_Birth === "CA" &&
      !birthCertificateFormData.County_Of_Birth);

  const calculateExpirationDateDifference = (expDate) => {
    const today = new Date();
    const difference = expDate.getTime() - today.getTime();
    return Math.ceil(difference / (1000 * 3600 * 24));
  };

  const getValidDays = (days) => {
    // This function converts days from DB into days accounting for leap years
    const date = new Date();

    // Year
    if (days >= 365) {
      date.setFullYear(date.getFullYear() + days / 365);
      // Month
    } else if (days >= 30) {
      date.setMonth(date.getMonth() + days / 30);
      // Day
    } else {
      date.setDate(date.getDate() + days);
    }

    // Convert date value back into days
    return (date - new Date()) / (1000 * 3600 * 24);
  };

  const validateExpirationDate = (expirationDate, date) => {
    const maxDays = expirationDate?.maxDays;
    const minDays = expirationDate?.minDays;
    const diffDays = calculateExpirationDateDifference(new Date(date));

    if (maxDays === undefined && minDays === undefined) {
      // Default behavior more than 2 days from today, but less than 3652.5 days (10 years) from today.
      return diffDays >= 2 && diffDays < 3652.5;
    }

    const isValidMax = maxDays ? diffDays <= getValidDays(maxDays) : true;
    const isValidMin = minDays ? diffDays >= getValidDays(minDays) : true;

    // If no minYear is defined, assume we only allow today
    if (minDays === undefined) {
      return diffDays >= 0 && isValidMax;
    }

    return isValidMax && isValidMin;
  };

  // is the value between the min/max
  const isValidInputLength = (value, categoryId) => {
    const category = getPassportCategory(categoryId);
    const minLength = MIN_LENGTH[category];
    const maxLength = MAX_LENGTH[category];
    return value.length >= minLength && value.length <= maxLength;
  };

  const emptyInputs =
    passportFormData.Passport_Number.length === 0 ||
    passportFormData.Passport_Expiration_Date.length === 0;

  const inputMismatch = (atdStatus) => {
    if (isAbbyyFlow(atdStatus) || !isMobile) {
      return (
        passportFormData.Passport_Number !==
        passportFormData.Confirm_Passport_Number
      );
    }
    return false;
  };
  /**
   * Checks Passport form fields are not empty
   * Checks Passport numbers match and expiration is 30+ days from today
   * Used to enable/disable the modal submit button
   */
  const validPassportForm = (expirationDate, atdStatus) => {
    return (
      !emptyInputs &&
      validateExpirationDate(
        expirationDate,
        passportFormData?.Passport_Expiration_Date
      ) &&
      !inputMismatch(atdStatus)
    );
  };

  const isValidPassportForm = (expirationDate, categoryId, atdStatus) => {
    return (
      validPassportForm(expirationDate, atdStatus) &&
      isValidInputLength(passportFormData.Passport_Number, categoryId)
    );
  };

  const isValidExpirationDateForm = (expirationDate) => {
    const expDate = expirationDateFormData?.Expiration_Date;
    const passportExpDate = passportFormData?.Passport_Expiration_Date;

    let userExpirationDate;
    if (expDate && expDate !== "--") {
      userExpirationDate = expDate;
    } else if (passportExpDate && passportExpDate !== "--") {
      userExpirationDate = passportExpDate;
    }

    return (
      validateExpirationDate(expirationDate, userExpirationDate) &&
      userExpirationDate.length === 10
    );
  };

  return useMemo(
    () => ({
      validBCForm,
      isValidPassportForm,
      isValidInputLength,
      isValidExpirationDateForm,
      validateExpirationDate,
    }),
    [
      validBCForm,
      isValidPassportForm,
      isValidInputLength,
      isValidExpirationDateForm,
      validateExpirationDate,
    ]
  );
}
